import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import { inboxReviewKeys } from "hooks/api/query-keys";

import { getTouchpointVersionHistory } from "generated/api/touchpoint-version-history";
import type { TouchpointVersionHistoryDto } from "generated/schemas";

import { buildHistoryParams } from "hooks/api/use-inbox-reviews.helpers";

import { PaginatedRequestOptions } from "types/pagination";
import { normalizePageMetadata } from "utilities/pagination";
import { InboxFilter } from "types/inbox";

const inboxApi = getTouchpointVersionHistory();

export interface InboxReviewsResponse {
  items: TouchpointVersionHistoryDto[];
  totalElements: number;
  totalPages: number;
}

type UseInboxReviewsParams = PaginatedRequestOptions & {
  filters?: InboxFilter;
};

type UseQueryHookOptions<TQueryFnData, TData = TQueryFnData> = Omit<
  UseQueryOptions<TQueryFnData, Error, TData>,
  "queryKey" | "queryFn"
>;

export const useInboxReviews = <TData = InboxReviewsResponse>(
  params: UseInboxReviewsParams,
  options?: UseQueryHookOptions<InboxReviewsResponse, TData>,
) => {
  const { filters, ...paginationOptions } = params;

  return useQuery({
    queryKey: inboxReviewKeys.list({ ...paginationOptions, ...(filters && { filters }) }),
    queryFn: async (): Promise<InboxReviewsResponse> => {
      const apiParams = buildHistoryParams(paginationOptions, filters);
      const response = await inboxApi.getHistory(apiParams);

      const content: TouchpointVersionHistoryDto[] = (response.content ?? []).map(
        (item: TouchpointVersionHistoryDto & { validContentType?: boolean }) => ({
          ...item,
          isValidContentType: item.validContentType,
        }),
      );
      const meta = normalizePageMetadata(response.page, content.length);

      return {
        items: content,
        totalElements: meta.totalElements,
        totalPages: meta.totalPages,
      };
    },
    ...options,
  });
};
